/* eslint-disable indent */
import {
  pathOr,
  prop,
  includes,
  pipe,
  allPass,
  all,
  complement,
  pathEq,
  any,
  both,
  propEq,
  isNil,
  propOr,
  pathSatisfies,
  not,
  find,
  path,
  ifElse,
  has,
  filter,
  map,
  split,
  last,
  trim,
  curry,
  modulo,
  __,
  gt,
  flatten,
  equals,
  anyPass,
  lensPath,
  set,
  clone,
  findIndex,
  hasPath,
  always,
  cond,
  T,
  lt,
  replace,
  multiply,
  isEmpty,
  join,
  values,
  reduce,
  concat
} from 'ramda'

import { utils } from '@digital-commerce/shared-utils'
import { formatCurrency } from './currency'

const COUPON_CODE = 'Coupon Code'

const { getTopLevelItems, getHSService, _getItems, getMonthlyItemPrice, findMonthlyRecurring } =
  utils.cart

const _getIds = (item) => {
  const result = { id: item.id }
  if (item.tags.includes('security')) result.type = 'SHS'
  return result
}

const _validateItems = (items) => items.filter((item) => item && item.id && item.tags)

const _getSubItemsIds = (item) => {
  const subItems = pathOr([], ['items'])(item)
  const validatedSubItems = _validateItems(subItems)

  return validatedSubItems.map(_getIds)
}

export const getIdsToRemoveFromCart = (items) => {
  if (!items) return []
  const itemsArray = Array.isArray(items) ? items : [items]

  const itemsWithIdAndTags = _validateItems(itemsArray)

  const topLevelIds = itemsWithIdAndTags.map(_getIds)

  const businessConnectItem = itemsWithIdAndTags.find(({ tags }) =>
    tags.includes('business-connect')
  )

  const hasBusinessConnectItem = Boolean(businessConnectItem)

  if (!hasBusinessConnectItem) return topLevelIds

  const bConnSubItemsIds = _getSubItemsIds(businessConnectItem)

  return [...topLevelIds, ...bConnSubItemsIds]
}

export const isAffiliateFlow = pathEq(['root', 'customer', 'mobility', 'segment'], 'AFFILIATE')

export const hasTosPartnershipTags = pipe(
  getTopLevelItems,
  any(
    both(
      pipe(prop('tags'), allPass([includes('tos-partnership'), includes('coupon')])),
      propEq('category', 'HS_OFFER')
    )
  )
)

const getSmartHomeSecurity = getHSService(['security'])

export const hasSmartHomeSecurity = pipe(getSmartHomeSecurity, complement(isNil))

export const isTOS = pipe(
  propOr([], 'tags'),
  allPass([includes('tos-standalone'), includes('online-security')])
)

export const isHSExistingCustomer = pathSatisfies(pipe(isNil, not), [
  'root',
  'customer',
  'hs',
  'ban'
])

export const hasFIFAProvisioner = (cart) =>
  pathOr([], ['root', 'provisioners'])(cart).includes('FIFA')

const findFirstMonth = (recurring) =>
  find(
    allPass([
      pathEq(['recurringPeriod', 'every'], 1),
      pathEq(['recurringPeriod', 'unit'], 'MONTH'),
      propEq('recurringDuration', 1)
    ])
  )(recurring) || {}

export const getFirstMonthItemPrice = pipe(path(['itemPrice', 'recurring']), findFirstMonth)

export const getCartCouponCodes = (cart) => {
  const itemsId = Object.keys(cart.items)
  const cartItems = itemsId.map((id) => cart.items[id])

  const cartCouponItems = cartItems.filter((item) => {
    return item.productOffering.characteristics.some(
      (characteristic) => characteristic.displayName === 'Coupon Code'
    )
  })

  const extractCouponCode = (characteristic) =>
    characteristic.displayName === 'Coupon Code' ? characteristic.value : undefined

  const characteristicDisplayNames = cartCouponItems.flatMap((item) =>
    item.productOffering.characteristics.map((characteristic) => extractCouponCode(characteristic))
  )

  const cartCouponCodes = characteristicDisplayNames.filter((item) => item !== undefined)

  return cartCouponCodes
}
export const isCodeRedemptionEnabled = (featureFlags) => {
  return (
    featureFlags && featureFlags.enableCodeRedemption && featureFlags.showAppliedPromoCodeDetails
  )
}

const getPromoCodeName = pipe(pathOr('', ['productOffering', 'name']), split(':'), last, trim)

const getServiceDiscounts = pipe(
  pathOr([], ['itemPrice', 'recurring']),
  find(has('alterations')),
  propOr([], 'alterations'),
  filter(pathEq(['category'], 'DISCOUNT')),
  map(prop('name'))
)

const getCouponCode = pipe(
  pathOr([], ['productOffering', 'characteristics']),
  find(propEq('displayName', COUPON_CODE)),
  pathOr([], ['value'])
)

export const getHSPromoCode = pipe(getTopLevelItems, find(pipe(prop('tags'), includes('coupon'))))

export const getManuallyAppliedPromoCode = (service, promoCodeItem) =>
  pipe(
    getServiceDiscounts,
    ifElse(
      includes(getPromoCodeName(promoCodeItem)),
      () => getCouponCode(promoCodeItem),
      () => undefined
    )
  )(service)

export const getHSCredits = curry((service, cart) => {
  return pipe(
    _getItems,
    filter(pipe(prop('tags'), allPass([includes(service), includes('credit')])))
  )(cart)
})

export const filterExistingActions = filter(pipe(propEq('action', 'existing'), not))

export const getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration = pipe(
  map(
    pipe(
      pathOr([], ['itemPrice', 'recurring']),
      find(
        allPass([
          pathEq(['recurringPeriod', 'every'], 1),
          pathEq(['recurringPeriod', 'unit'], 'MONTH'),
          pipe(prop('recurringDuration'), modulo(__, 12), equals(0))
        ])
      ),
      propOr([], 'alterations'),
      filterExistingActions,
      filter(pipe(prop('recurringDuration'), modulo(__, 12), gt(__, 0)))
    )
  ),
  flatten
)

export const isAppointmentRequired = pipe(
  _getItems,
  any(
    allPass([
      pipe(prop('tags'), includes('work-offer')),
      pipe(prop('action'), anyPass([equals('ADD'), equals('MODIFY')]))
    ])
  )
)

const cartHasItemWithTag = (tag) => (product) => {
  return pipe(propOr([], 'tags'), includes(tag))(product)
}

const hasListOfTags = (tags) => (item) => {
  return tags.every((tag) => cartHasItemWithTag(tag)(item))
}

export const getItemWithTags =
  (tags) =>
  (items = []) => {
    return filter(hasListOfTags(tags))(items)
  }

const findMonthlyRecurringIndex = (recurring) =>
  findIndex(
    allPass([
      pathEq(['recurringPeriod', 'every'], 1),
      pathEq(['recurringPeriod', 'unit'], 'MONTH'),
      complement(propEq('recurringDuration', 1))
    ])
  )(recurring)

const findFirstMonthIndex = (recurring) =>
  findIndex(
    allPass([
      pathEq(['recurringPeriod', 'every'], 1),
      pathEq(['recurringPeriod', 'unit'], 'MONTH'),
      propEq('recurringDuration', 1)
    ])
  )(recurring)

const updatePriceforExistingItem = (existingItem, item, isMonthlyPayment) => {
  const itemPrice = isMonthlyPayment
    ? getMonthlyItemPrice(item).amount
    : getFirstMonthItemPrice(item).amount
  const itemPriceIndex = isMonthlyPayment
    ? findMonthlyRecurringIndex(existingItem.itemPrice.recurring)
    : findFirstMonthIndex(existingItem.itemPrice.recurring)
  const existingItemPrice = existingItem.itemPrice.recurring[itemPriceIndex]
  if (existingItemPrice) {
    existingItemPrice.amount += existingItemPrice ? itemPrice : 0
    const monthlyItemPrice = lensPath(['recurring', itemPriceIndex])
    set(monthlyItemPrice, existingItemPrice, existingItem.itemPrice.recurring)
  }
}

const groupItemsOfferId = (items, isMonthlyPayment) => {
  const itemsByKey = {}
  const clonedItems = clone(items)
  clonedItems.forEach((item) => {
    const existingItem = itemsByKey[item.productOffering.id]
    if (existingItem) {
      updatePriceforExistingItem(existingItem, item, isMonthlyPayment)
      existingItem.quantity += 1
    } else {
      itemsByKey[item.productOffering.id] = item
    }
  })
  return Object.values(itemsByKey)
}

const _adjustQuantityAndAmount = (item) => {
  if (item.quantity > 1) {
    const cloneItem = clone(item)
    const correctAmount = getMonthlyItemPrice(item).amount / item.quantity
    if (hasPath(['itemPrice', 'recurring', 0, 'amount'], cloneItem)) {
      cloneItem.itemPrice.recurring[0].amount = correctAmount
    }
    cloneItem.quantity = 1
    return cloneItem
  }
  return item
}

export const groupMonthlyItemsByOfferId = (items) => {
  const adjustedItems = items.map(_adjustQuantityAndAmount)
  return groupItemsOfferId(adjustedItems, true)
}

export const getFirstMonthPromotionsOfItemChargedOnlyFirstMonth = pipe(
  path(['itemPrice', 'recurring']),
  findFirstMonth,
  propOr([], 'alterations'),
  filter(propEq('recurringDuration', 1))
)

/**
 * Renamed from hideButtonTosPartnershipOrBreach
 * */
export const isTOSPartnershipOrTOSBreachFlow = (featureFlags, cart) => {
  const itemHasTosGuardianTag = pipe(
    both(
      pipe(prop('tags'), includes('online-security-guardian-breach')),
      propEq('category', 'HS_OFFER')
    )
  )

  const hasTosGuardianTag = pipe(getTopLevelItems, any(itemHasTosGuardianTag))

  // Is TOS Partnership
  const isTosPartnershipFlow = () => {
    const isHsTOSPartnershipToggleOn = pathOr(false, ['isTosPartnershipEnabled'])
    const hasTosStandalone = pipe(_getItems, any(isTOS))
    const hasSkipCreditCheckTag = pipe(
      _getItems,
      any(pipe(prop('tags'), includes('verified-coupon')))
    )

    return (
      isHsTOSPartnershipToggleOn(featureFlags) &&
      hasTosStandalone(cart) &&
      hasSkipCreditCheckTag(cart) &&
      !hasTosGuardianTag(cart)
    )
  }

  // Is TOS Breach
  const isTosBreachFlow = () => {
    const isHsTOSBreachToggleOn = pathOr(false, ['tosBreach'])
    return isHsTOSBreachToggleOn(featureFlags) && hasTosGuardianTag(cart)
  }

  return isTosPartnershipFlow() || isTosBreachFlow()
}

const checkPropTagForMHDiscount = pipe(
  ifElse(has('tags'), pipe(prop('tags'), complement(includes('m-and-h-discount'))), always(true))
)

export const getRecurringPromotionsForNoTermService = cond([
  [
    pathEq(['contractTerm', 'recurringDuration'], 0),
    pipe(
      pathOr([], ['itemPrice', 'recurring']),
      findMonthlyRecurring,
      propOr([], 'alterations'),
      filter(
        pipe(
          allPass([
            pipe(path(['recurringDuration']), gt(__, 0)),
            pipe(path(['recurringDuration']), lt(__, 12)),
            checkPropTagForMHDiscount
          ])
        )
      )
    )
  ],
  [T, always([])]
])

export const FLOW_TYPES = {
  REGULAR: 'REGULAR',
  RESERVATION: 'RESERVATION'
}

export const getFlowType = (cart) => {
  return pipe(
    _getItems,
    find(
      allPass([
        pathEq(['fulfillment', 'inStorePickupEligible'], true),
        pathEq(['fulfillment', 'method'], 'IN_STORE')
      ])
    ),
    ifElse(isNil, always(FLOW_TYPES.REGULAR), always(FLOW_TYPES.RESERVATION))
  )(cart)
}

export const hasOnlyMobilityAccessories = (cart) => {
  return pipe(
    getTopLevelItems,
    filter(complement(pathEq(['category'], 'MOBILITY_SHIPPING'))),
    allPass([complement(isEmpty), all(pathEq(['category'], 'MOBILITY_ACCESSORY'))])
  )(cart)
}

export const hasMobilityOfferType = curry((orderType, cart) => {
  return pipe(
    _getItems,
    pipe(
      filter(
        allPass([propEq('category', 'MOBILITY_OFFER'), pathEq(['productOrder', 'type'], orderType)])
      ),
      complement(isEmpty)
    )
  )(cart)
})

export const getValidationErrors = pathOr([], ['root', 'validationErrors'])

export const hasEligibilityFulfillmentMethods = (cart) =>
  not(isEmpty(pathOr([], ['root', 'eligibleFulfillmentMethods'], cart)))

export const isCartValid = (cart) => {
  const hasValidationErrors = not(isEmpty(getValidationErrors(cart)))

  return !hasValidationErrors && hasEligibilityFulfillmentMethods(cart)
}

// @TODO: should be moved to mobility.js file - see ticket: DCUI-1068
export const isIMEIDuplicated = pipe(
  pathOr([], ['root', 'validationErrors']),
  find(propEq('message', 'RETURN_DEVICES_DUPLICATE_IMEI_FOUND')),
  pipe(isNil, not)
)

const itemHasTosGuardianTag = pipe(
  both(
    pipe(prop('tags'), includes('online-security-guardian-breach')),
    propEq('category', 'HS_OFFER')
  )
)

export const hasTosGuardianTag = pipe(getTopLevelItems, any(itemHasTosGuardianTag))

export const hasSkipCreditCheckTag = (cart) => {
  return pipe(_getItems, any(pipe(prop('tags'), includes('verified-coupon'))))(cart)
}

const getCancellationFee = (service) => ({
  cancellationFeeAmount: path(['contractTerm', 'cancellationFee', 'amount'], service),
  cancellationFeeAmortizedAmount: path(
    ['contractTerm', 'cancellationFee', 'amortizedAmount'],
    service
  )
})

export const transformCancellationFeeContent = curry((service, lang, content) => {
  const { cancellationFeeAmount, cancellationFeeAmortizedAmount } = getCancellationFee(service)

  return pipe(
    replace('#cancellationFeeAmount#', formatCurrency(cancellationFeeAmount, lang)),
    replace(
      '#cancellationFeeAmortizedAmount#',
      formatCurrency(cancellationFeeAmortizedAmount, lang)
    ),
    replace(
      '#partialCancellationFeeAmortizedAmount#',
      formatCurrency(multiply(cancellationFeeAmortizedAmount, 0.5), lang)
    )
  )(content)
})

export const isMonthlyRecurringAmountNotZero = pipe(
  pathOr([], ['itemPrice', 'recurring']),
  findMonthlyRecurring,
  prop('amount'),
  gt(__, 0)
)
export const hasGift = (items) => {
  if (!Array.isArray(items)) {
    return false
  }
  return pipe(find(pipe(prop('tags'), includes('gift'))), complement(isNil))(items)
}

export const translateFootnoteKeys = (translate) => pipe(map(translate), join(''))

export const getCartType = (cart) => {
  const provisioners = path(['root', 'provisioners'])(cart)
  const lineOfBusiness = {
    ESS: 'mobility',
    UPC: 'mobility',
    FIFA: 'hs',
    COMPASS: 'hs'
  }
  const result = provisioners.map((provisioner) => {
    return lineOfBusiness[provisioner]
  })
  const uniqueResults = [...new Set(result)]
  const cartType = uniqueResults[0]
  return uniqueResults.length > 1 ? 'mixed' : cartType
}

export const getFirstMonthAddons = filter(
  pipe(
    path(['itemPrice', 'recurring']),
    findMonthlyRecurring,
    allPass([complement(isEmpty), propEq('recurringDuration', 1)])
  )
)

export const getProductName = ifElse(
  hasPath(['productOffering', 'name']),
  path(['productOffering', 'name']),
  ifElse(
    propEq('category', 'MOBILITY_CONNECTION_FEE'),
    always('Mobility Connection Fee'),
    path(['productOffering', 'displayName'])
  )
)

export const getContractLength = (item) => {
  const contractLength = pathOr(0, ['contractTerm', 'recurringDuration'], item)
  return contractLength
}

export const isSHSCart = (cart) =>
  pipe(
    propOr({}, 'items'),
    values,
    any(
      pipe(
        prop('tags'),
        allPass([
          includes('service-plan'),
          includes('security'),
          ifElse(includes('smartwear'), always(false), always(true)),
          ifElse(includes('smartwear-device'), always(false), always(true))
        ])
      )
    )
  )(cart)

export const isItemValid = curry((cart, cartItem) => {
  if (!cartItem) return true

  const { id } = cartItem
  const cartValidationErrors = getValidationErrors(cart)

  if (not(isEmpty(cartValidationErrors))) {
    const invalidItems = pipe(
      map((e) => e.items),
      reduce(concat, [])
    )(cartValidationErrors)

    return !includes(id, invalidItems)
  }

  return true
})

export const getCacheKey = (scope) => (cart) => {
  const timestamp = cart?.timestamp || Date.now()
  return `${scope}-${timestamp}`
}

export const removeDuplicateVisitorStateCookie = (removeCookie, documentCookies) => {
  if (!documentCookies) return
  // Grabs number of visitorState cookies
  const visitorStateCookieCount = documentCookies.split('visitorState=').length - 1
  if (visitorStateCookieCount > 1 && removeCookie) {
    // Will always delete the current domain cookie (prod: www.telus.com)
    removeCookie('visitorState', {
      path: '/'
    })
  }
}

export const isAffiliateCampaign = (cart) => {
  return (cart?.root?.order?.[0]?.system?.fifaInformation?.campaigns ?? []).includes('affiliates-evs')
}
